<template>
  <section
    class="service-page"
    :class="{ loaded: pageLoaded }"
  >

    <!-- HERO -->
    <section class="hero fade-up delay-1">

      <div class="hero-content">

        <span class="eyebrow">
          Ecommerce • Marketplaces • Automatización
        </span>

        <h1 class="hero-title">
          Sistemas de Ventas Digitales
        </h1>

        <p class="hero-description">
          Implementamos ecommerce, automatizaciones y sistemas comerciales
          para que tu negocio venda de forma profesional,
          organizada y escalable.
        </p>

        <div class="hero-actions">

          <button
            class="primary-btn"
            type="button"
            :disabled="loading"
            @click="goToServiceForm"
          >

            <span
              v-if="loading"
              class="loader"
            />

            {{ loading ? "Cargando..." : "Solicitar Diagnóstico" }}

          </button>

          <div class="hero-badges">

            <span
              v-for="badge in badges"
              :key="badge"
            >
              {{ badge }}
            </span>

          </div>

        </div>

      </div>

      <div class="hero-image fade-scale delay-2">

        <img
          src="/images/img12.jpg"
          alt="Sistemas de ventas digitales"
          loading="lazy"
          decoding="async"
        />

      </div>

    </section>

    <!-- PROBLEMS -->
    <section class="problems fade-up delay-2">

      <div class="section-heading">

        <span class="section-label">
          Problemas comunes
        </span>

        <h2>
          Lo que resolvemos
        </h2>

      </div>

      <div class="problem-grid">

        <article
          v-for="(problem, i) in problems"
          :key="i"
          class="problem-card card-hover"
        >

          <span class="problem-icon">
            ✕
          </span>

          <p>
            {{ problem }}
          </p>

        </article>

      </div>

    </section>

    <!-- SOLUTIONS -->
    <section class="solutions fade-up delay-3">

      <div class="section-heading">

        <span class="section-label">
          Soluciones principales
        </span>

        <h2>
          Servicios incluidos
        </h2>

      </div>

      <div class="solution-grid">

        <article
          v-for="solution in solutions"
          :key="solution.title"
          class="solution-card card-hover"
        >

          <div class="solution-icon">
            {{ solution.icon }}
          </div>

          <h3>
            {{ solution.title }}
          </h3>

          <p>
            {{ solution.description }}
          </p>

          <ul>

            <li
              v-for="(item, i) in solution.features"
              :key="i"
            >
              ✓ {{ item }}
            </li>

          </ul>

        </article>

      </div>

    </section>

    <!-- MICRO SERVICES -->
    <section class="microservices fade-up delay-4">

      <div class="section-heading">

        <span class="section-label">
          Microservicios
        </span>

        <h2>
          Servicios específicos
        </h2>

      </div>

      <div class="micro-grid">

        <article
          v-for="service in microservices"
          :key="service.title"
          class="micro-card card-hover"
        >

          <div class="micro-top">

            <h3>
              {{ service.title }}
            </h3>

            <span class="micro-category">
              {{ service.category }}
            </span>

          </div>

          <p>
            {{ service.description }}
          </p>

          <span class="price">
            {{ service.price }}
          </span>

        </article>

      </div>

    </section>

    <!-- BENEFITS -->
    <section class="benefits fade-up delay-5">

      <div class="section-heading center">

        <span class="section-label">
          Beneficios
        </span>

        <h2>
          Lo que obtiene tu negocio
        </h2>

      </div>

      <div class="benefit-grid">

        <article
          v-for="(benefit, i) in benefits"
          :key="i"
          class="benefit-card card-hover"
        >

          <span>
            ✓
          </span>

          <p>
            {{ benefit }}
          </p>

        </article>

      </div>

    </section>

    <!-- PROCESS -->
    <section class="process fade-up delay-6">

      <div class="section-heading center">

        <span class="section-label">
          Nuestro proceso
        </span>

        <h2>
          Cómo trabajamos
        </h2>

      </div>

      <div class="process-grid">

        <article
          v-for="step in process"
          :key="step.number"
          class="process-card card-hover"
        >

          <span class="step-number">
            {{ step.number }}
          </span>

          <h3>
            {{ step.title }}
          </h3>

          <p>
            {{ step.description }}
          </p>

        </article>

      </div>

    </section>

    <!-- CTA -->
    <section class="cta-section fade-up delay-7">

      <div class="cta-card">

        <span class="cta-label">
          Ecommerce • Automatización • Marketplaces
        </span>

        <h2>
          Lleva tu negocio al siguiente nivel digital
        </h2>

        <p>
          Diseñamos sistemas de ventas digitales adaptados
          a la operación y crecimiento de tu negocio.
        </p>

        <button
          class="primary-btn secondary"
          type="button"
          :disabled="loading"
          @click="goToServiceForm"
        >

          <span
            v-if="loading"
            class="loader"
          />

          {{ loading ? "Cargando..." : "Cotizar Proyecto" }}

        </button>

      </div>

    </section>

  </section>
</template>

<script>
import { useHead } from "@vueuse/head";

export default {
  name: "DigitalSalesSystems",

  data() {
    return {
      loading: false,
      pageLoaded: false,

      badges: Object.freeze([
        "WooCommerce",
        "Mercado Libre",
        "Amazon Seller",
        "Google Analytics",
        "CRM",
        "Automatización"
      ]),

      problems: Object.freeze([
        "Ventas solamente por WhatsApp",
        "Pedidos desorganizados",
        "No tienes ecommerce",
        "Marketplace mal configurado",
        "Seguimiento manual a clientes",
        "Falta de automatización comercial"
      ]),

     solutions: Object.freeze([
  {
    icon: "🛒",
    title: "Tienda en Línea",
    description: "Construimos tu canal de venta propio, listo para recibir pedidos y pagos desde el día uno.",
    features: [
      "Tienda online profesional",
      "Configuración de pagos y envíos",
      "WhatsApp integrado",
      "SEO básico para productos",
      "Optimización de experiencia de compra",
    ],
  },
  {
    icon: "📦",
    title: "Marketplaces",
    description: "Te posicionamos en Mercado Libre y Amazon para vender donde ya están tus clientes.",
    features: [
      "Publicación en Mercado Libre",
      "Configuración Amazon Seller",
      "Catálogo inicial de productos",
      "Configuración operativa completa",
      "Capacitación para gestión diaria",
    ],
  },
  {
    icon: "📈",
    title: "Optimización Comercial",
    description: "Analizamos y mejoramos tu operación de ventas para convertir más con menos fricción.",
    features: [
      "Google Analytics y métricas comerciales",
      "Optimización de conversión",
      "Automatizaciones comerciales",
      "Integraciones con herramientas externas",
      "Dashboards de seguimiento",
    ],
  },
]),

      microservices: Object.freeze([
        {
          category: "Ecommerce",
          title: "WooCommerce",
          description:
            "Configuración ecommerce profesional.",
                  },
        {
          category: "Marketplace",
          title: "Mercado Libre",
          description:
            "Configuración y optimización inicial.",        },
        {
          category: "Marketplace",
          title: "Amazon Seller",
          description:
            "Implementación y organización comercial.",        },
        {
          category: "Automatización",
          title: "CRM Comercial",
          description:
            "Gestión y seguimiento automatizado.",        },
        {
          category: "Analítica",
          title: "Google Analytics",
          description:
            "Métricas ecommerce y conversiones.",
        },
        {
          category: "Optimización",
          title: "Checkout Optimization",
          description:
            "Mejora de experiencia y conversión.",        }
      ]),

      benefits: Object.freeze([
        "Vende 24/7",
        "Centraliza clientes y pedidos",
        "Reduce tareas manuales",
        "Mayor control operativo",
        "Mejor seguimiento comercial",
        "Escalabilidad digital"
      ]),

      process: Object.freeze([
        {
          number: "01",
          title: "Diagnóstico",
          description:
            "Analizamos tu operación y objetivos."
        },
        {
          number: "02",
          title: "Planeación",
          description:
            "Definimos herramientas y arquitectura digital."
        },
        {
          number: "03",
          title: "Implementación",
          description:
            "Configuramos ecommerce e integraciones."
        },
        {
          number: "04",
          title: "Optimización",
          description:
            "Mejoramos rendimiento y conversión."
        }
      ])
    };
  },

  mounted() {
    requestAnimationFrame(() => {
      this.pageLoaded = true;
    });
  },

  setup() {
    useHead({
      title: "Sistemas de Ventas Digitales | BearTech",
      htmlAttrs: {
        lang: "es-MX"
      },
      meta: [
        {
          name: "description",
          content:
            "Implementación de ecommerce, marketplaces y automatización comercial para negocios."
        }
      ]
    });
  },

  methods: {
    async goToServiceForm() {

      if (this.loading) return;

      this.loading = true;

      try {

        await this.$router.push(
          "/servicios/ventas-digitales/formulario"
        );

      } finally {

        this.loading = false;

      }
    }
  }
};
</script>

<style scoped>

/* ===== BASE ===== */

.service-page {
  overflow: hidden;

  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #ffffff 100%
    );

  color: #0f172a;
}

/* ===== GLOBAL ===== */

section {
  padding: 5rem 1.5rem;
}

.section-heading {
  margin-bottom: 3rem;
}

.section-heading.center {
  text-align: center;
}

.section-label {
  display: inline-block;

  margin-bottom: 1rem;
  padding: .45rem 1rem;

  border-radius: 999px;

  background: #ccfbf1;
  color: #0f766e;

  font-size: .85rem;
  font-weight: 700;
}

.section-heading h2 {
  font-size: clamp(2rem, 4vw, 3rem);
  color: #012719;
}

/* ===== HERO ===== */

.hero {
  max-width: 1300px;
  margin: auto;

  min-height: 92vh;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
}

.eyebrow {
  display: inline-block;

  margin-bottom: 1.5rem;

  color: #0f766e;
  font-weight: 700;
}

.hero-title {
  font-size: clamp(3rem, 7vw, 5.5rem);

  line-height: .95;

  margin-bottom: 1.5rem;

  color: #012719;
}

.hero-description {
  max-width: 620px;

  font-size: 1.15rem;
  line-height: 1.9;

  color: #475569;
}

.hero-actions {
  margin-top: 2rem;
}

.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: .8rem;

  margin-top: 1.5rem;
}

.hero-badges span {
  padding: .65rem 1rem;

  border-radius: 999px;

  background: white;
  border: 1px solid #dbe4ea;


  transition:
    transform .25s ease,
    border-color .25s ease;
}



.hero-image {
  overflow: hidden;

  border-radius: 32px;

  box-shadow:
    0 30px 70px rgba(0,0,0,.12);
}

.hero-image img {
  width: 100%;
  height: 760px;

  object-fit: cover;

  display: block;

  transition:
    transform .8s ease;
}

.hero-image:hover img {
  transform: scale(1.04);
}

/* ===== BUTTON ===== */

.primary-btn{
  position:relative;
  overflow:hidden;

  padding:1rem 2rem;

  border:none;
  border-radius:14px;

  background:
    linear-gradient(
      135deg,
      #051b19,
      #5a665e
    );

  color:white;
  font-weight:700;

  cursor:pointer;

  transition:
    transform .25s ease,
    opacity .25s ease,
    box-shadow .25s ease;
}

.primary-btn::before{
  content:"";

  position:absolute;
  top:0;
  left:-120%;

  width:100%;
  height:100%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,.25),
      transparent
    );

  transition:left .6s ease;
}

.primary-btn:hover::before{
  left:120%;
}

.primary-btn:hover{
  transform:translateY(-4px);

  box-shadow:
    0 15px 30px rgba(15,118,110,.3);
}

.primary-btn:disabled{
  opacity:.6;
  cursor:not-allowed;
}

/* ===== LOADER ===== */

.loader {
  width: 18px;
  height: 18px;

  border-radius: 50%;

  border:
    2px solid rgba(255,255,255,.3);

  border-top-color: white;

  animation:
    spin .7s linear infinite;
}

/* ===== LAYOUT ===== */

.problems,
.solutions,
.microservices,
.benefits,
.process {
  max-width: 1300px;
  margin: auto;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid,
.process-grid {
  display: grid;
  gap: 2rem;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid {
  grid-template-columns: repeat(3, 1fr);
}

.process-grid {
  grid-template-columns: repeat(4, 1fr);
}

/* ===== CARDS ===== */

.problem-card,
.solution-card,
.micro-card,
.benefit-card,
.process-card {
  background: white;

  border-radius: 24px;

  padding: 2rem;

  border: 1px solid #e2e8f0;
}

.card-hover {
  transition:
    transform .3s ease,
    box-shadow .3s ease,
    border-color .3s ease;
}

.card-hover:hover {
  transform: translateY(-6px);

  border-color: #ccfbf1;

  box-shadow:
    0 20px 40px rgba(0,0,0,.08);
}

/* ===== PROBLEMS ===== */

.problem-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.problem-icon {
  font-size: 1.5rem;
  color: #ef4444;
}

/* ===== SOLUTIONS ===== */

.solution-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.solution-card h3 {
  margin-bottom: 1rem;
  color: #012719;
}

.solution-card p {
  margin-bottom: 1.5rem;

  line-height: 1.7;
  color: #475569;
}

.solution-card ul {
  padding-left: 1rem;
}

.solution-card li {
  margin-bottom: .8rem;
}

/* ===== MICRO ===== */

.micro-top {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 1rem;

  margin-bottom: 1rem;
}

.micro-card h3 {
  color: #012719;
}

.micro-category {
  padding: .4rem .8rem;

  border-radius: 999px;

  background: #ecfeff;
  color: #0f766e;

  font-size: .75rem;
  font-weight: 700;
}

.micro-card p {
  line-height: 1.7;
  color: #475569;
}

.price {
  display: inline-block;

  margin-top: 1.5rem;

  color: #0f766e;
  font-weight: 800;
}

/* ===== BENEFITS ===== */

.benefit-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.benefit-card span {
  color: #0f766e;
  font-size: 1.4rem;
}

/* ===== PROCESS ===== */

.step-number {
  display: inline-flex;

  width: 65px;
  height: 65px;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #ccfbf1;
  color: #0f766e;

  font-weight: 800;

  margin-bottom: 1.5rem;
}

/* ===== CTA ===== */

.cta-section {
  padding-bottom: 6rem;
}

.cta-card {
  max-width: 1100px;
  margin: auto;

  text-align: center;

  padding: 5rem 2rem;

  border-radius: 32px;

  background:
    linear-gradient(
      135deg,
      #012719,
      #0f766e
    );

  color: white;
}

.cta-label {
  display: inline-block;

  margin-bottom: 1rem;

  opacity: .85;
}

.cta-card h2 {
  font-size: clamp(2rem, 5vw, 4rem);

  margin-bottom: 1rem;
}

.cta-card p {
  max-width: 700px;

  margin:
    auto
    auto
    2rem;

  line-height: 1.8;

  opacity: .92;
}

/* ===== PAGE ENTRANCE ===== */

.fade-up,
.fade-scale {
  opacity: 0;
}

.loaded .fade-up {
  animation:
    fadeUp .9s ease forwards;
}

.loaded .fade-scale {
  animation:
    fadeScale 1s ease forwards;
}

.delay-1 {
  animation-delay: .1s !important;
}

.delay-2 {
  animation-delay: .25s !important;
}

.delay-3 {
  animation-delay: .4s !important;
}

.delay-4 {
  animation-delay: .55s !important;
}

.delay-5 {
  animation-delay: .7s !important;
}

.delay-6 {
  animation-delay: .85s !important;
}

.delay-7 {
  animation-delay: 1s !important;
}

/* ===== ANIMATIONS ===== */

@keyframes fadeUp {

  from {
    opacity: 0;
    transform:
      translateY(40px);
  }

  to {
    opacity: 1;
    transform:
      translateY(0);
  }
}

@keyframes fadeScale {

  from {
    opacity: 0;
    transform:
      scale(.92);
  }

  to {
    opacity: 1;
    transform:
      scale(1);
  }
}

@keyframes spin {

  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ===== RESPONSIVE ===== */

@media (max-width: 1100px) {

  .hero,
  .problem-grid,
  .solution-grid,
  .micro-grid,
  .benefit-grid,
  .process-grid {
    grid-template-columns: 1fr;
  }

  .hero {
    min-height: auto;
  }

  .hero-image img {
    height: 500px;
  }
}

@media (max-width: 768px) {

  section {
    padding: 4rem 1rem;
  }

  .hero-title {
    font-size: 2.9rem;
  }

  .hero-image img {
    height: 350px;
  }

  .hero-description {
    font-size: 1rem;
  }

  .primary-btn {
    width: 100%;
    justify-content: center;
  }

  .cta-card {
    padding: 4rem 1.5rem;
  }
}

</style>